import {
  EOrderDeliveryExpeditionType,
  EOrderDeliveryStatus,
  EOrderHistoryType,
  EOrderShippingMethod,
  EOrderShippingOperator,
  IHub,
  IOrder,
  IOrderDelivery,
  IOrderHistory,
  IPaging,
  EUserRoles,
} from '../../../types'

export namespace CheckUserExists {
  export type Request = {
    email: string
  }

  export type Response = {
    exists: boolean
  }
}

export namespace CreateUser {
  export type Request = {
    email: string
    password: string
  }

  export type Response = {
    access_token: string
    created_at: string
    email: string
    name: string | null
    surname: string | null
    phone_number: string | null
    uuid: string
    hubs: Array<{
      uuid: string
      name: string
      document: string
      trading_name: string
      address: {
        street_name: string
        address_number: string
        complement?: string | undefined
        postal_code: string
        neighborhood_name: string
        city_name: string
        province_code: string
      }
      operation_times: Array<{
        uuid: string
        weekday: number
        open: string
        close: string
        is_active: boolean
      }>
    }>
  }
}

export namespace RequestForgotPasswordEmailLink {
  export type Params = {
    email: string
    url: string
  }

  export type Response = 'ok'
}

export namespace UpdatePassword {
  export type Request = {
    email: string
    password: string
    token: string
  }

  export type Response = {
    access_token: string
    created_at: string
    email: string
    hubs: Array<{
      uuid: string
      name: string
      document: string
      trading_name: string
      address: {
        street_name: string
        address_number: string
        complement?: string
        postal_code: string
        city_name: string
        province_code: string
        neighborhood_name: string
      }
      operation_times: Array<{
        uuid: string
        weekday: number
        open: string
        close: string
        is_active: boolean
      }>
    }>
    name: string | null
    surname: string | null
    phone_number: string | null
    uuid: string
  }
}

export namespace Login {
  export type Request = {
    email: string
    password: string
    token_cloudflare?: string
    code_2fa?: string
  }

  export type Response = {
    access_token: string
    created_at: string
    email: string
    hubs: Array<{
      uuid: string
      name: string
      document: string
      trading_name: string
      address: {
        uuid: string
        street_name: string
        address_number: string
        complement?: string
        postal_code: string
        city_name: string
        province_code: string
        neighborhood_name: string
      }
      operation_times: Array<{
        uuid: string
        weekday: number
        open: string
        close: string
        is_active: boolean
      }>
    }>
    name: string | null
    surname: string | null
    phone_number: string | null
    uuid: string
    is_super_admin: boolean
  }
}

export namespace LoadUser {
  export type Response = {
    created_at: string
    email: string
    hubs: Array<{
      uuid: string
      name: string
      document: string
      trading_name: string
      address: {
        uuid: string
        street_name: string
        address_number: string
        complement?: string
        postal_code: string
        city_name: string
        province_code: string
        neighborhood_name: string
      }
      operation_times: Array<{
        uuid: string
        weekday: number
        open: string
        close: string
        is_active: boolean
      }>
    }>
    name: string | null
    surname: string | null
    phone_number: string | null
    uuid: string
  }
}

export namespace LoadHub {
  export type Params = {}

  export type Response = {
    uuid: string
    name: string
    trading_name: string
    document: string
    address: {
      uuid: string
      street_name: string
      address_number: string
      postal_code: string
      neighborhood_name: string
      city_name: string
      province_code: string
      complement?: string
    }
    contacts: Array<{
      name: string
      phones: Array<{
        phone: string
        is_whatsapp: boolean
      }>
      emails: Array<{
        email: string
      }>
    }>
    users: Array<{
      uuid: string
      name: string
    }>
    operation_times: Array<{
      uuid: string
      is_active: boolean
      weekday: number
      open: string
      close: string
    }>
  }
}

export namespace UpdateHub {
  export type Params = {
    uuid: string
    name: string
    trading_name: string
    document: string
    address: {
      uuid?: string
      street_name: string
      address_number: string
      postal_code: string
      neighborhood_name: string
      city_name: string
      province_code: string
      complement?: string
    }
    operation_times: {
      uuid?: string
      is_active: boolean
      weekday: number
      opening_hours: {
        uuid?: string
        open: string
        close: string
      }[]
    }[]
    users: {
      uuid: string
    }[]
    contacts: {
      name: string
      emails: {
        email: string
      }[]
      phones: {
        phone: string
        is_whatsapp: boolean
      }[]
    }[]
  }

  export type Response = {
    uuid: string
    name: string
    trading_name: string
    document: string
    address: {
      uuid: string
      street_name: string
      address_number: string
      postal_code: string
      neighborhood_name: string
      city_name: string
      province_code: string
      complement?: string
    }
    contacts: Array<{
      name: string
      phones: Array<{
        phone: string
        is_whatsapp: boolean
      }>
      emails: Array<{
        email: string
      }>
    }>
    users: Array<{
      uuid: string
      name: string
    }>
    operation_times: Array<{
      uuid: string
      is_active: boolean
      weekday: number
      open: string
      close: string
    }>
  }
}

export namespace LoadFeatureFlags {
  export type Response = {
    data: {
      name: string
      uuid: string
    }[]
  }
}

export namespace LoadKYC {
  export type Response = {
    data: {
      account_token: 'baas'
      created_at: string
      data: KycData
      status: KYCStatus
      motive?: string
      documents: Array<CompanyDoc | UserDoc>
      response: 'baas'
      updated_at: string
      uuid: string
    }
  }

  export enum KYCStatus {
    Started = 'kyc_started',
    Pending = 'kyc_pending',
    Finished = 'kyc_finished',
    Approved = 'kyc_approved',
    Error = 'kyc_rejected',
  }

  export enum KYCDocStatus {
    Uploaded = 'waiting_baas_upload',
    Processing = 'documents_sent',
    Success = 'approved',
    Error = 'documents_error',
  }

  export enum DocType {
    BALANCO = 'BALANCO',
    CONTRATO_SOCIAL = 'CONTRATO_SOCIAL',
    CNH = 'CNH',
    RG = 'RG',
    SELFIE = 'SELFIE',
  }

  export enum BusinessOwnerType {
    Owner = 'SOCIO',
    Representative = 'REPRESENTANTE',
    Other = 'DEMAIS_SOCIOS',
  }

  export type CompanyDoc = {
    name: DocType.BALANCO | DocType.CONTRATO_SOCIAL
    status: KYCDocStatus
    info: {
      front: string
    }
    url: string
    uuid: string
  }

  export type UserDoc = {
    name: DocType.CNH | DocType.RG
    status: KYCDocStatus
    url: string
    info: {
      front: string
      back: string
    }
  }

  export type KycData = {
    business_name: string
    document_number: string
    trading_name: string
    email: string
    phone: string
    address: {
      city: string
      complement?: string
      neighborhood: string
      number: string
      postal_code: string
      state: string
      street: string
    }
    business_owners: Array<{
      name: string
      document: string
      phone: string
      email: string
      mother_name: string
      social_name: string
      dob: string
      type: BusinessOwnerType
      address: {
        postal_code: string
        street: string
        number: string
        complement?: string
        city: string
        state: string
        neighborhood: string
      }
      pep: boolean
    }>
  }
}

export namespace UpdateKYC {
  export type Request = {
    business_name: string
    document_number: string
    trading_name: string
    email: string
    phone: string
    address: {
      city: string
      complement?: string
      neighborhood: string
      number: string
      postal_code: string
      state: string
      street: string
    }
    business_owners: Array<{
      name: string
      document: string
      phone: string
      email: string
      mother_name: string
      social_name: string
      dob: string
      address: {
        postal_code: string
        street: string
        number: string
        complement?: string
        city: string
        state: string
        neighborhood: string
      }
      pep: boolean
    }>
    documents: Array<
      | {
          type: DocType.BALANCO | DocType.CONTRATO_SOCIAL
          file_front: File | string
        }
      | {
          type: DocType.CNH | DocType.RG
          file_front: File | string
          file_back: File | string
        }
      | {
          type: DocType.SELFIE
          file_front: File | string
        }
    >
  }

  export type Response = {
    data: {
      body: {
        clientCode: string
        documentNumber: string
        proposalId: string
        status: string
      }
      version: string
    }
  }

  export enum KYCStatus {
    Started = 'kyc_started',
    Pending = 'kyc_pending',
    Finished = 'kyc_finished',
    Approved = 'kyc_approved',
    Error = 'kyc_rejected',
  }

  export enum KYCDocStatus {
    Uploaded = 'waiting_baas_upload',
    Processing = 'documents_sent',
    Success = 'approved',
    Error = 'documents_error',
  }

  export enum DocType {
    BALANCO = 'BALANCO',
    CONTRATO_SOCIAL = 'CONTRATO_SOCIAL',
    CNH = 'CNH',
    RG = 'RG',
    SELFIE = 'SELFIE',
  }

  export enum BusinessOwnerType {
    Owner = 'SOCIO',
    Representative = 'REPRESENTANTE',
    Other = 'DEMAIS_SOCIOS',
  }

  export type CompanyDoc = {
    name: DocType.BALANCO | DocType.CONTRATO_SOCIAL
    status: KYCDocStatus
    info: {
      front: string
    }
    url: string
    uuid: string
  }

  export type UserDoc = {
    name: DocType.CNH | DocType.RG
    status: KYCDocStatus
    url: string
    info: {
      front: string
      back: string
    }
  }

  export type KycData = {
    business_name: string
    document_number: string
    trading_name: string
    email: string
    phone: string
    address: {
      city: string
      complement?: string
      neighborhood: string
      number: string
      postal_code: string
      state: string
      street: string
    }
    business_owners: Array<{
      name: string
      document: string
      phone: string
      email: string
      mother_name: string
      social_name: string
      dob: string
      type: BusinessOwnerType
      address: {
        postal_code: string
        street: string
        number: string
        complement?: string
        city: string
        state: string
        neighborhood: string
      }
      pep: boolean
    }>
  }
}

export namespace LoadProductPackages {
  export type Request = {
    base_available?: boolean
    category_uuid?: string
    has_sales_price?: boolean
    load?: string
    option_values?: string
    order_by?: string
    order_direction?: string
    is_available?: boolean
    page: number
    per_page?: number
    price_from?: number
    price_to?: number
    search?: string
  }

  export type Response = IPaging<{
    uuid: string
    ean: string
    transfer_value: number
    product: {
      name: 'Produto Teste'
      image: string
    }
    option_values: Array<{
      name: string
    }>
  }>
}

export namespace LoadOrdersToAnswer {
  export type Request = {}

  export type Response = IPaging<{
    id: number
    uuid: string
    expeditionType: EOrderDeliveryExpeditionType
    deliveryLeadTime: string
    answerLeadTime: string
    orderType: EOrderShippingMethod
    items: Array<{
      quantity: number
      name: string
      image?: string
    }>
  }>
}

export namespace LoadOrdersToSeparate {
  export type Request = {
    search?: string
    paginate?: boolean
    page?: number
  }

  export type Response = IPaging<{
    id: number
    uuid: string
    expeditionType: EOrderDeliveryExpeditionType
    deliveryLeadTime: string
    answerLeadTime: string | null
    transferTotal: number
    orderType: EOrderShippingMethod
    status: EOrderDeliveryStatus
    shippingOperator: EOrderShippingOperator
    steps: Array<{
      label: 'invoice' | 'descriptive' | 'label'
      done: boolean
    }>
  }>
}

export namespace LoadOrdersToPickUp {
  export type Request = {
    search?: string
    paginate?: boolean
    page?: number
  }

  export type Response = IPaging<{
    id: number
    ordersCount: number
    logisticOperator: EOrderShippingOperator
    vehiclePlate: string | null
    driverName: string | null
    status: EOrderDeliveryStatus
    orders: Array<{
      orderType: EOrderShippingMethod
      id: number
      uuid: string
      deliveryLeadTime: string
      expeditionType: EOrderDeliveryExpeditionType
      transferTotal: number
      steps: Array<{
        label: 'invoice' | 'descriptive' | 'label'
        done: boolean
      }>
    }>
  }>
}

export namespace LoadOrdersToDropOff {
  export type Request = {
    search?: string
    paginate?: boolean
    page?: number
  }

  export type Response = IPaging<{
    id: number
    ordersCount: number
    logisticOperator: EOrderShippingOperator
    vehiclePlate: string | null
    driverName: string | null
    status: EOrderDeliveryStatus
    orders: Array<{
      orderType: EOrderShippingMethod
      id: number
      uuid: string
      deliveryLeadTime: string
      expeditionType: EOrderDeliveryExpeditionType
      transferTotal: number
      steps: Array<{
        label: 'invoice' | 'descriptive' | 'label'
        done: boolean
      }>
    }>
  }>
}

export namespace LoadOrdersInTransit {
  export type Request = {
    search?: string
    paginate?: boolean
    page?: number
  }

  export type Response = IPaging<{
    id: number
    ordersCount: number
    logisticOperator: EOrderShippingOperator
    vehiclePlate: string | null
    driverName: string
    status: EOrderDeliveryStatus
    orders: Array<{
      id: number
      uuid: string
      deliveryLeadTime: string
      expeditionType: EOrderDeliveryExpeditionType
      orderType: EOrderShippingMethod
      transferTotal: number
      steps: Array<{
        label: 'invoice' | 'descriptive' | 'label'
        done: boolean
      }>
    }>
  }>
}

export namespace LoadOrdersCompleted {
  export type Request = {
    search?: string
    paginate?: boolean
    page?: number
    per_page?: number
    dateFrom?: string
    dateTo?: string
  }

  export type Response = IPaging<{
    status: EOrderDeliveryStatus
    id: number
    uuid: string
    orderType: EOrderShippingMethod
    expeditionType: EOrderDeliveryExpeditionType
    shippingOperator: EOrderShippingOperator
    deliveryLeadTime: string
    answerLeadTime: string | null
    steps: Array<{
      label: 'invoice' | 'descriptive' | 'label'
      done: boolean
    }>
  }>
}

export namespace LoadOrdersLost {
  export type Request = {
    paginate?: boolean
    page?: number
    search?: string
    order_by?: 'delivery_estimated'
    order_direction?: 'asc' | 'desc'
    per_page?: number
  }

  export type Response = IPaging<{
    deliveryLeadTime: string
    expeditionType: EOrderDeliveryExpeditionType
    shippingOperator: EOrderShippingOperator
    id: number
    orderType: EOrderShippingMethod
    status: EOrderHistoryType
    steps: Array<{
      label: 'invoice' | 'descriptive' | 'label'
      done: boolean
    }>
    transferTotal: number
    uuid: string
  }>
}

export namespace LoadOrderLost {
  export type Request = {}

  export type Response = {
    data: {
      id: number
      uuid: string
      subtotal: number
      delivery_cost: number
      total: number
      customer: string
      ordered_at: string
      items: Array<{
        total: number
        quantity: number
        package_price: number
        product: {
          uuid: string
          name: string
          ean: string
          image: string
        }
        option_values: Array<{
          name: string
        }>
      }>
      shipping_method: EOrderShippingMethod
      status: 'lost' | 'refused'
    }
  }
}

export namespace PostOrderAnswer {
  type AcceptParams = {
    type: EOrderHistoryType.Accepted
    own_delivery: boolean
  }

  export type RefuseParams = {
    type: EOrderHistoryType.Refused
    details: {
      type: string
      message: string | null
      item: string
    }[]
  }

  export type Params = AcceptParams | RefuseParams

  export type Response = IOrderHistory
}

export namespace LoadOrder {
  export type Response = IOrder
}

export namespace UpdateOrderDeliveryInvoice {
  export type Params = {
    invoice_file: string | File
  }

  export type Response = IOrderDelivery
}

export namespace LoadOrderLabel {
  export type Response = Blob
}

export namespace LoadTransactions {
  export type Params = {
    page: number
    search: string
    per_page?: number
    status?: 'pending' | 'processed'
    order_by: 'date'
    order_direction: 'desc' | 'asc'
  }

  export type Response = {
    data: {
      transactions: IPaging<{
        amount: number
        date: string
        description: string
        status: 'pending'
        type: 'incoming' | 'outgoing'
        updated_at: string
        uuid: string
      }>
    }
  }
}

export namespace LoadTransaction {
  export type Params = {}

  export type Response = {
    data: {
      transaction: {
        amount: number
        date: string
        description: string
        status: 'pending' | 'processed'
        type: 'incoming' | 'outgoing'
        updated_at: string
        uuid: string
        transfers: Array<Transfer>
        info: Infotransfer
      }
    }
  }

  type Transfer = {
    uuid: string
    due_date: string
    value: string
    created_at: string
    updated_at: string
    order: Order
  }

  type Order = {
    ordered_at: string
  }

  type Infotransfer = {
    transactionId: number
    code: string
    slip: string
    slipAuth: string
    endToEndId: string
  }
}

export namespace LoadBalancePending {
  export type Params = {}

  export type Response = {
    data: {
      totalPending: number
    }
  }
}

export namespace LoadBalancePaid {
  export type Params = {}

  export type Response = {
    data: {
      totalPaid: number
    }
  }
}

export namespace PostHubs {
  export type Request = {
    name: string
    trading_name: string
    document: string
    affiliate_uuid?: string
    users: {
      uuid: string
    }[]
    address: {
      address_number: string
      city_name: string
      complement?: string
      country_code?: string
      neighborhood_name: string
      postal_code: string
      province_code: string
      street_name: string
    }
    contacts: {
      name: string
      phones: {
        phone: string
        is_whatsapp: boolean
      }[]
      emails: {
        email: string
      }[]
    }[]
    accept_terms: boolean
    commercial_conditions: boolean
    accept_terms_2: boolean
  }

  export type Response = IHub
}

export namespace FetchHubsExists {
  export type Request = {
    document: string
  }

  export type Response = {
    exists: boolean
  }
}

export namespace FetchUserByHub {
  export type Response = Array<{
    uuid: string
    name: string | null
    surname: string | null
    email: string
    pivot: {
      user_role: {
        uuid: string
        name: EUserRoles
      }
    }
  }>
}

export namespace GetUserRoles {
  export type Response = IPaging<{
    uuid: string
    name: EUserRoles
  }>
}

export namespace PostInviteUser {
  export type Request = {
    email: string
    name: string
    surname: string
    pivot: {
      user_role: {
        uuid: string
      }
    }
  }

  export type Response = {
    uuid: string
    name: string
    email: string
  }
}

export namespace PutUserHub {
  export type Request = {
    email: string
    name: string
    surname: string
    pivot: {
      user_role: {
        uuid: string
      }
    }
  }

  export type Response = {
    uuid: string
    name: string
    email: string
  }
}

export namespace DeleteUserHub {
  export type Response = {
    success: boolean
  }
}

export namespace GetTermStatus {
  export type Response = {
    uuid: string
    term: string
    due_date: string
  }
}

export namespace PostTermStatus {
  export type Request = {
    term_uuid: string
    validation_code: string
  }

  export type Response = {
    uuid: string
    due_date: string
    term: string
    created_at: string
    updated_at: string
  }
}

export namespace PostSendTermCode {
  export type Request = {
    term_uuid: string
  }

  export type Response = {
    success: boolean
  }
}

export namespace GetMetabaseToken {
  export type Response = {
    token: string
    iframeUrl: string
  }
}

export namespace Logout {
  export type Response = {
    success: boolean
  }
}

export interface IDeliveryOwnAcceptN8N {
  staging: boolean
  hub_uuid: string
  order_uuid: string
  order_id: string
}
